/**
 * Typed API client. Every call validates the response against the shared contract, so a server
 * drift fails loudly here instead of as `undefined` deep in a component.
 */

import {
  AlertEventsOutput,
  AlertListOutput,
  AlertSchema,
  BasketListOutput,
  BasketSchema,
  CandlesOutput,
  type CloseBasketInput,
  type CreateAlertInput,
  type CreateJournalInput,
  ErrorBody,
  type ErrorCode,
  JournalEntrySchema,
  JournalListOutput,
  MarketOverviewOutput,
  MeOutput,
  type PatchBasketInput,
  type PatchJournalInput,
  type PatchSettingsInput,
  type PatchTokenInput,
  type PushSubscribeInput,
  type PutBuyFillInput,
  type PutSellFillInput,
  SearchOutput,
  SectorDetailOutput,
  SectorRadarOutput,
  SettingsSchema,
  type StartBasketInput,
  type SyncNowInput,
  SyncStatusOutput,
  TokenListOutput,
  TokenSchema,
  TopCoinsOutput,
  VapidOutput,
} from "@shared/contracts";
import type { SectorLabel } from "@shared/sectors";
import type { z } from "zod";

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  constructor(code: ErrorCode, message: string, status: number, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  schema: z.ZodType<T> | null,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let parsed: z.infer<typeof ErrorBody> | null = null;
    try {
      parsed = ErrorBody.parse(await res.json());
    } catch {
      parsed = null;
    }
    throw new ApiError(
      parsed?.error ?? "INTERNAL",
      parsed?.message ?? `HTTP ${res.status}`,
      res.status,
      parsed?.requestId,
    );
  }
  if (schema === null) return undefined as T;
  return schema.parse(await res.json());
}

const q = (params: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
};

export const api = {
  auth: {
    me: () => request("GET", "/auth/me", MeOutput),
    login: (password: string) => request("POST", "/auth/login", null, { password }),
    logout: () => request("POST", "/auth/logout", null),
  },
  tokens: {
    list: () => request("GET", "/tokens", TokenListOutput),
    add: (cgId: string) => request("POST", "/tokens", TokenSchema, { cgId }),
    patch: (id: string, input: z.infer<typeof PatchTokenInput>) =>
      request("PATCH", `/tokens/${id}`, TokenSchema, input),
    remove: (id: string) => request("DELETE", `/tokens/${id}`, null),
    candles: (id: string) => request("GET", `/tokens/${id}/candles`, CandlesOutput),
  },
  market: {
    overview: () => request("GET", "/market/overview", MarketOverviewOutput),
    search: (query: string) => request("GET", `/market/search${q({ q: query })}`, SearchOutput),
    top: () => request("GET", "/market/top", TopCoinsOutput),
  },
  baskets: {
    list: () => request("GET", "/baskets", BasketListOutput),
    get: (id: string) => request("GET", `/baskets/${id}`, BasketSchema),
    create: (tokenId: string) => request("POST", "/baskets", BasketSchema, { tokenId }),
    patch: (id: string, input: PatchBasketInput) => request("PATCH", `/baskets/${id}`, BasketSchema, input),
    remove: (id: string) => request("DELETE", `/baskets/${id}`, null),
    start: (id: string, input: z.infer<typeof StartBasketInput> = {}) =>
      request("POST", `/baskets/${id}/start`, BasketSchema, input),
    close: (id: string, input: z.infer<typeof CloseBasketInput> = {}) =>
      request("POST", `/baskets/${id}/close`, BasketSchema, input),
    putBuyFill: (id: string, rungIndex: number, input: PutBuyFillInput) =>
      request("PUT", `/baskets/${id}/buy-fills/${rungIndex}`, BasketSchema, input),
    putSellFill: (id: string, tpIndex: number, input: PutSellFillInput) =>
      request("PUT", `/baskets/${id}/sell-fills/${tpIndex}`, BasketSchema, input),
  },
  journal: {
    list: (params: { basketId?: string; limit?: number } = {}) =>
      request("GET", `/journal${q(params)}`, JournalListOutput),
    create: (input: z.infer<typeof CreateJournalInput>) =>
      request("POST", "/journal", JournalEntrySchema, input),
    patch: (id: string, input: z.infer<typeof PatchJournalInput>) =>
      request("PATCH", `/journal/${id}`, JournalEntrySchema, input),
    remove: (id: string) => request("DELETE", `/journal/${id}`, null),
  },
  alerts: {
    list: () => request("GET", "/alerts", AlertListOutput),
    create: (input: z.infer<typeof CreateAlertInput>) => request("POST", "/alerts", AlertSchema, input),
    patch: (id: string, active: boolean) => request("PATCH", `/alerts/${id}`, AlertSchema, { active }),
    remove: (id: string) => request("DELETE", `/alerts/${id}`, null),
    events: (limit = 50) => request("GET", `/alerts/events${q({ limit })}`, AlertEventsOutput),
  },
  sectors: {
    radar: () => request("GET", "/sectors", SectorRadarOutput),
    detail: (label: SectorLabel) => request("GET", `/sectors/${label}`, SectorDetailOutput),
  },
  settings: {
    get: () => request("GET", "/settings", SettingsSchema),
    patch: (input: z.infer<typeof PatchSettingsInput>) =>
      request("PATCH", "/settings", SettingsSchema, input),
  },
  push: {
    vapidPublicKey: () => request("GET", "/push/vapid-public-key", VapidOutput),
    subscribe: (input: z.infer<typeof PushSubscribeInput>) => request("POST", "/push/subscribe", null, input),
  },
  sync: {
    status: () => request("GET", "/sync/status", SyncStatusOutput),
    now: (input: z.infer<typeof SyncNowInput> = { what: "all" }) => request("POST", "/sync/now", null, input),
  },
  export: {
    /** Browser navigation URL — not fetched through JSON. */
    fillsCsvUrl: (year: number) => `/api/export/fills.csv?year=${year}`,
  },
};

export type Api = typeof api;
